/**
 * crashLogger.js — Persists unhandled errors to Firestore `crashLogs` collection.
 * Safe to call from anywhere (ErrorBoundary, global handlers) — never throws.
 * Logs are viewable in Firebase Console → Firestore → crashLogs.
 *
 * Rate limits:
 *  - 10s dedup for identical errors (in-memory)
 *  - Max 20 crash logs per device per day (localStorage counter, resets at midnight)
 *  - `expiresAt` field set 14 days ahead — configure Firestore TTL policy on this field
 *    to auto-delete old documents (Firebase Console → Firestore → TTL Policies)
 */
import { db, auth } from "../firebase";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { version, commitHash, buildTime } from "../buildInfo";
import { isStaleChunkMessage, isUrllessChunkMessage, CHUNK_URL_IN_MESSAGE } from "./staleChunkPattern";
import { getInFlightChunkNames } from "./lazyChunk";
import { localStore, sessionStore } from "./safeStorage";
import { claimLatePopupAssertion } from "./popupAuthOutcome";

const MAX_STACK_LENGTH = 4000;
const DEDUP_MS = 10_000;
const DAILY_CAP = 20;
const TTL_DAYS = 14;
const DAILY_KEY = "sendly-crash-count";
// Durable crash queue: crashes are persisted here synchronously so a stale-chunk
// recovery reload, a PWA kill, or a dead Firestore connection can't lose them
// before they reach the server. flushPendingCrashes() (run on boot) resends.
const PENDING_KEY = "sendly-pending-crashes";
const PENDING_CAP = 10;

// In-memory dedup state
let lastLoggedKey = "";
let lastLoggedTime = 0;

/** Check and increment the daily per-device counter. Returns false if cap reached. */
function withinDailyCap() {
  try {
    const today = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
    const raw = localStore.get(DAILY_KEY);
    let data = raw ? JSON.parse(raw) : null;
    // Reset if stored date differs from today
    if (!data || data.d !== today) {
      data = { d: today, n: 0 };
    }
    if (data.n >= DAILY_CAP) return false;
    data.n += 1;
    localStore.set(DAILY_KEY, JSON.stringify(data));
    return true;
  } catch (_) {
    // unparseable counter — allow the log (an unavailable storage never reaches here: it just cannot count)
    return true;
  }
}

/** Unique id for a queued crash record (local queue management only). */
function makeCrashId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* fall through */ }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readPending() {
  return localStore.getJSON(PENDING_KEY, []);
}
function writePending(arr) {
  localStore.setJSON(PENDING_KEY, arr.slice(-PENDING_CAP));
}
function enqueuePending(record) {
  const arr = readPending();
  arr.push(record);
  writePending(arr);
}
function dropPending(id) {
  writePending(readPending().filter((r) => r && r.id !== id));
}

/** Write one crash record to Firestore (always a `create` — see firestore.rules). */
async function writeCrash(record) {
  const expiresAt = Timestamp.fromDate(new Date(Date.now() + TTL_DAYS * 24 * 60 * 60 * 1000));
  // Exactly 13 fields — the crashLogs rule caps document size at 13 keys.
  await addDoc(collection(db, "crashLogs"), {
    message: record.message,
    stack: record.stack || "",
    url: record.url,
    uid: record.uid,
    userAgent: record.userAgent,
    source: record.source,
    componentStack: record.componentStack || "",
    extra: record.extra || null,
    timestamp: Timestamp.fromMillis(record.timestampMs || Date.now()),
    expiresAt,
    appVersion: record.appVersion || null,
    commitHash: record.commitHash || null,
    buildTime: record.buildTime || null,
  });
}

/**
 * Flush crash records persisted by a previous session that couldn't reach the
 * server (reload race / dead Firestore connection). Call once on boot. Stops at
 * the first failure (still offline / unauthenticated) and keeps the rest for a
 * later flush. Records are sent with their ORIGINAL timestamp so a late flush
 * still shows when the crash actually happened.
 */
export async function flushPendingCrashes() {
  const arr = readPending();
  if (!arr.length) return;
  const remaining = [];
  let stop = false;
  for (const record of arr) {
    if (!record || !record.id) continue; // drop malformed entries
    if (stop) { remaining.push(record); continue; }
    try {
      await writeCrash(record);
    } catch (e) {
      // A record Firestore refuses as data can never be written: drop it and go
      // on. Kept, it would stop every later flush of this device right here.
      if (isPermanentWriteError(e)) continue;
      stop = true;            // still offline → stop and keep the rest
      remaining.push(record);
    }
  }
  writePending(remaining);
}

/**
 * A write that can never succeed, whenever it is retried: the SDK refused the
 * data itself (`invalid-argument` — e.g. a nested array, which Firestore does
 * not store). Offline, unavailable or permission errors are not permanent.
 */
export function isPermanentWriteError(e) {
  return !!e && e.code === "invalid-argument";
}

/**
 * Log an error to Firestore crashLogs collection.
 * @param {Error|string} error - The error object or message
 * @param {object} [extra] - Additional context (componentStack, source, etc.)
 */
export async function logCrashError(error, extra = {}) {
  try {
    const message = error?.message || String(error);
    const stack = (error?.stack || "").slice(0, MAX_STACK_LENGTH);

    // Ignore errors from browser extensions (MetaMask, etc.)
    if (/chrome-extension:|moz-extension:|safari-extension:/.test(stack)) return;

    // Cloudflare Web Analytics / RUM beacon (edge-injected, not in our source).
    // It's served with CORS so the browser does NOT redact it to "Script error."
    // — we get the full message + stack, which slips past the cross-origin filter
    // below. The beacon calls ES2022 .at() which throws on old browsers
    // ("...at is not a function"); that's entirely Cloudflare's code and harmless
    // to us (a failed analytics ping), nothing for us to fix.
    if (/cloudflareinsights\.com|beacon\.min\.js/.test(stack)) return;

    // Firebase Auth iframe context — Firebase Hosting reserves /__/auth/* on
    // every connected domain, but if the custom-domain SSL/connection isn't
    // fully provisioned the request falls through to our `** → /index.html`
    // rewrite and our SPA boots inside the auth iframe. Lazy chunks then fail
    // (sandbox/cross-origin), and SW registration fails too. None of this is
    // actionable from our code — it's a Hosting config issue at the domain
    // level, not a runtime app bug. Filter both errors plus any rejection
    // logged from that URL.
    if (typeof window !== "undefined" && /\/__\/auth\//.test(window.location.pathname)) return;

    // Service worker fetch failure — transient (network blip, sandboxed
    // context). The browser auto-retries the SW registration on the next
    // navigation; nothing for us to fix.
    if (/^Script\s.+\/sw\.js load failed$/.test(message)) return;

    // Firestore IndexedDB connection drop — iOS Safari / PWA suspend/resume
    // artifact. The SDK reconnects automatically on the next operation
    // (and useVisibilityResume forces a disableNetwork→enableNetwork toggle on
    // resume to rebuild the dead connection); the user sees nothing wrong.
    // Recoverable noise, no signal.
    if (/Connection to Indexed Database server lost/.test(message)) return;

    // Sibling WebKit wording: the IndexedDB backend process itself failed
    // (suspend/resume, low disk). It arrives as a stackless DOMException, so
    // there is nothing to attribute — and by construction it is never ours:
    // Firestore runs memory-only, and our one IndexedDB user (judgeQueue) never
    // leaks: its background calls end in .catch, and every judge / JAM save is
    // awaited inside a try/catch that shows an error toast (CompetitionJudgeModal,
    // CompetitionAttemptModal) — a lost tap is visible to the judge, not hidden
    // here. A judgeQueue caller that drops its catch would make this filter
    // swallow a real data loss, so keep those catches. What does leak are Firebase's own
    // background IndexedDB calls (Analytics → Installations, Auth's storage
    // poll) — nothing user-visible, nothing for us to fix.
    if (/An internal error was encountered in the Indexed Database server/.test(message)) return;

    // Third-party scripts injected into PAGE context (so the extension-URL
    // filter above misses them — their stack points at our own URL):
    //  - Firefox iOS "Reader mode" probes `window.__firefox__.reader`
    //  - crypto-wallet extensions (MetaMask et al.) poke `window.ethereum`
    // Neither is our code; nothing to fix. Match on the tell-tale global name.
    if (/__firefox__|window\.ethereum|ethereum\.selectedAddress/.test(message)) return;

    // Android WebView JS-bridge teardown — the WebView's OWN native bridge raises
    // "Error invoking postMessage: <java reason>" when a @JavascriptInterface call
    // fails, which is what happens while an in-app browser tears the view down: the
    // Facebook IAB's injected `iabjs://navigation_performance_logger_android` fires
    // its beforeunload beacon (_handleBrowserPreparingToClose → sendBeforeUnloadMessage
    // → sendDataToNative) into a bridge the host app is already dismantling. We see
    // it only on URLs carrying an `fbclid`. Match on the BRIDGE prefix, not the Java
    // reason — two wordings so far ("Java object is gone" = the object was already
    // collected, "Java exception was raised during method invocation" = the call
    // reached a dying object), and the reason half is the host app's, so it will keep
    // drifting. Our own postMessage calls (SW messaging in useServiceWorkerUpdate /
    // index.html) can never produce this wording; they throw DOMExceptions. No app
    // code is on the stack, nothing is recoverable from JS, and the user has already
    // left the page. Same family as the __firefox__ / window.ethereum filters above —
    // pure container noise that would otherwise eat the 20/device/day cap.
    if (/Error invoking postMessage:/.test(message)) return;

    // Transient Firebase offline read — the client briefly lost connectivity
    // (often during the chunk-recovery reload). The SDK recovers on reconnect;
    // not an app bug. Sibling of the IndexedDB filter above.
    if (/client is offline/.test(message)) return;

    // Firebase Installations couldn't reach the network — raised from the
    // Analytics init path (`initializeAnalytics` awaits
    // `Promise.all([dynamicConfig, installations.getId()])`) when the client is
    // offline at that moment. The only consequence is that this page load
    // sends no GA event; nothing user-visible breaks and there is nothing to
    // fix in our code. Same family as the 'client is offline' filter above.
    if (/installations\/app-offline/.test(message)) return;

    // Dedup identical errors in quick succession
    const dedupKey = message + (extra.source || "");
    const now = Date.now();
    if (dedupKey === lastLoggedKey && now - lastLoggedTime < DEDUP_MS) return;
    lastLoggedKey = dedupKey;
    lastLoggedTime = now;

    // Daily per-device cap
    if (!withinDailyCap()) return;

    const record = {
      id: makeCrashId(),
      message,
      stack,
      url: typeof window !== "undefined" ? window.location.href : null,
      uid: auth.currentUser?.uid || null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      source: extra.source || "unknown",
      componentStack: (extra.componentStack || "").slice(0, MAX_STACK_LENGTH),
      extra: extra.info || null,
      timestampMs: Date.now(),
      appVersion: version || null,
      commitHash: commitHash || null,
      buildTime: buildTime || null,
    };

    // Durable-first: persist SYNCHRONOUSLY before the (async) network write, so a
    // stale-chunk recovery reload, a PWA kill, or a dead Firestore connection
    // can't lose the crash. flushPendingCrashes() resends anything left here on
    // the next boot. Best-effort immediate write; drop from the queue on success.
    enqueuePending(record);
    try {
      await writeCrash(record);
      dropPending(record.id);
    } catch (e) {
      // Offline / interrupted — stays queued for the next flush. Refused data
      // never will be written: dropped, so it cannot block the queue.
      if (isPermanentWriteError(e)) dropPending(record.id);
    }
  } catch (_) {
    // Logging must never cause secondary failures
  }
}

/**
 * Detect stale chunk errors — deploy cache mismatch: the chunk is gone, or HTML
 * came back instead of JS. The message shapes (webpack's, the three native
 * import() wordings, Safari's MIME error) live in staleChunkPattern.js, shared
 * with ErrorBoundary, lazyChunk and the inline boot script.
 */
function isStaleChunkError(msg, filename) {
  if (isStaleChunkMessage(msg)) return true;
  // A CLASSIC script that received HTML (404/index.html) → the parser sees '<'.
  // A module script fails with the MIME error instead, covered above.
  if (/Unexpected token '<'/.test(msg) && /\.chunk\.js/.test(filename || "")) return true;
  return false;
}

/** Extract the failed asset URL from an error message / filename, if present. */
function extractChunkUrl(msg, filename) {
  const m = String(msg || "").match(CHUNK_URL_IN_MESSAGE);
  if (m) return m[0];
  if (/^https?:\/\//.test(filename || "")) return filename;
  return undefined;
}

/** Rewrite ONE poisoned HTTP-cache entry (deploy-era HTML pinned against a chunk URL). */
function healPoisonedChunk(url) {
  try {
    if (url && typeof fetch === "function") fetch(url, { cache: "reload" }).catch(() => {});
  } catch { /* fetch unavailable */ }
}

/**
 * Auto-reload once on stale chunk errors (30s cooldown to prevent infinite loops).
 * `healAll` — the failure named no URL but is a recognised chunk error (the two
 * Safari shapes): the recovery then heals every /static/js/ resource the page
 * knows instead of the one entry it cannot name. See index.html.
 */
function handleStaleChunkReload(failedUrl, healAll) {
  try {
    const key = "sendly-chunk-reload";
    const last = sessionStore.get(key);
    if (!last || Date.now() - Number(last) > 30000) {
      // No cooldown record can be kept (storage blocked or full) → no reload, or it would loop.
      if (!sessionStore.set(key, String(Date.now()))) return false;
      // Prefer the aggressive recovery exposed by index.html (unregister SW +
      // purge caches + cache-bust reload). A plain reload re-hits the
      // stale-while-revalidate SW still serving the old index.html (old chunk
      // hashes) and fails again — chunk failures that surface as
      // unhandledrejection (the background import() warm-ups in App.js, a
      // native import() nobody caught) reach only this path, so they need the
      // same escape route the ErrorBoundary already uses. Falls back to a plain
      // reload in dev / when the SW isn't present. Mirrors
      // ErrorBoundary.recoverFromChunkError.
      if (typeof window !== "undefined" && typeof window.__sendlyChunkRecovery === "function") {
        // Pass the failed chunk URL so the recovery rewrites that exact poisoned
        // HTTP-cache entry (`cache: 'reload'`) before reloading. `cause:
        // 'chunk'` lets the recovery pick its path by the service worker's
        // evidence (often a plain reload onto the already-cached new HTML).
        if (healAll && !failedUrl) window.__sendlyChunkRecovery(undefined, { cause: "chunk", healAll: true });
        else window.__sendlyChunkRecovery(failedUrl, { cause: "chunk" });
      } else {
        window.location.reload();
      }
      return true;
    }
    // Cooled down: no second reload — but still repair THIS entry. The cooldown
    // bounds RELOADS, not repairs, and the recovery only ever heals the URL it
    // was handed, so a client carrying more than one poisoned chunk never got
    // the rest fixed (2026-08-17: chunk 4013 failed twice inside the cooldown
    // that 7759 + 8147 had just opened, and stayed poisoned).
    healPoisonedChunk(failedUrl);
  } catch { /* recovery hook / reload unavailable */ }
  return false;
}

// ─────────────────────────────────────────────────────────────
// Boot-stall watchdog
//
// A white screen with NO thrown error never reaches the error-driven crash
// path above: it's a render gate stuck on <LoadingBlank> — a hung Firestore
// read in GymProvider's slug resolution (resolveSlug), a hung lazy-chunk fetch behind the root
// Suspense, or a dead-connection auth rehydrate. Nothing throws, so nothing is
// logged. This watchdog captures it: it waits for the app to signal a
// successful boot (window.__sendlyBooted, set once auth resolves) and, if that
// never happens while a full-page loader is still mounted
// (window.__sendlyLoaders > 0), records a diagnostic snapshot naming WHICH gate
// hung (window.__sendlyBootStage) plus network / SW / URL context.
//
// Durable-first is essential: the stall is usually a dead network connection,
// so the immediate Firestore write below would hang too. enqueuePending()
// persists to localStorage synchronously; flushPendingCrashes() (run on boot)
// resends it on the NEXT healthy open, where it surfaces in the platform-admin
// crash log with the failing build's commit hash + time.
const BOOT_STALL_MS = 20000;          // primary capture — well past a normal boot
const BOOT_STALL_PERSIST_MS = 40000;  // still stuck here = truly wedged, not just slow
// How long a page that spent time in the background must stay VISIBLE before we
// believe its loader is stuck rather than merely throttled (see below).
const BOOT_STALL_HIDDEN_GRACE_MS = 5000;
const BOOT_STALL_MAX_DEFERRALS = 3;   // a page that keeps re-backgrounding is judged eventually

// Sticky for the lifetime of the document: was this page ever in the background?
// Read by the snapshot, so a report that survives the deferral logic below still
// says whether backgrounding was in play.
let bootSawHidden = false;
let bootWatchdogCleanup = null;   // set by installBootWatchdog; used by the test seam

/** Test seam — mirrors `__setChunkDeadlineForTests` in utils/lazyChunk.js. */
export function __resetBootWatchdogForTests() {
  try { if (bootWatchdogCleanup) bootWatchdogCleanup(); } catch (_) { /* nothing to undo */ }
  bootWatchdogCleanup = null;
  bootSawHidden = false;
}

function bootStallSnapshot(persistent) {
  const s = { persistent: !!persistent };
  const set = (k, fn) => { try { s[k] = fn(); } catch (_) { /* field best-effort */ } };
  set("stage", () => window.__sendlyBootStage || "unknown");
  set("loaders", () => window.__sendlyLoaders || 0);
  set("rootKids", () => { const r = document.getElementById("root"); return r ? r.childElementCount : -1; });
  set("standalone", () => !!(window.matchMedia && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true)));
  set("online", () => navigator.onLine);
  set("vis", () => document.visibilityState);
  set("msSinceLoad", () => Math.round(performance.now()));
  set("sw", () => (navigator.serviceWorker && navigator.serviceWorker.controller && navigator.serviceWorker.controller.scriptURL) || null);
  set("lastGymUrl", () => localStore.get("sendly-last-gym-url"));
  // Set by index.html when it strips the `_swr` cache-buster: this boot came
  // out of a stale-chunk recovery reload (the param itself is gone from the URL).
  set("swrBoot", () => window.__sendlySwrBoot || null);
  set("swrPurge", () => sessionStore.get("sendly-sw-purge") || null);
  set("chunkReload", () => sessionStore.get("sendly-chunk-reload") || null);
  // Was the page ever backgrounded during the boot window? A hidden page has
  // throttled timers and deprioritised fetches, so it can sit on a loader
  // legitimately — the single most important thing to know when reading a report.
  set("hidden", () => bootSawHidden);
  // Chunk loads that STARTED but never finished — the field that is supposed to
  // name the hung request.
  //
  // This deliberately does NOT use Resource Timing: an entry is queued only when
  // a fetch COMPLETES, so an in-flight (or permanently hung) request never
  // appears there at all. The previous `!responseEnd` filter could therefore
  // never match anything — the field was silently always `[]`, including on the
  // two production /profile stalls it existed to explain, where it read as
  // "nothing is hanging" purely because it cannot express anything else.
  //
  // What the DOM can say: the module <script> for the main bundle and the
  // <link rel="modulepreload"> tags Vite adds — in index.html for main's static
  // imports, and at runtime for a lazy chunk that has at least one further
  // dependency. A tag still in the DOM with no completed timing entry IS the
  // in-flight request, by name. The "done" set is Resource Timing plus the
  // `/static/js/` set the inline boot script keeps (`window.__sendlyStaticJsSeen`,
  // fed by a PerformanceObserver): the timing buffer stops at 250 entries, and
  // past that every completed link would look pending. Absent in jsdom / when
  // the boot script did not run → Resource Timing alone.
  //
  // What the DOM CANNOT say: a leaf chunk (one that imports only from main)
  // gets no link at all under Vite, and native import() adds no <script>. For
  // those the list carries the NAMES lazyChunk is still waiting on — every
  // React.lazy in the app goes through it. Only a chunk whose request started
  // more than BOOT_STALL_MS − CHUNK_DEADLINE_MS after load can still be there
  // at snapshot time; anything earlier was deadlined into a recovery first.
  set("pending", () => {
    const done = new Set((performance.getEntriesByType("resource") || []).map(r => String(r.name)));
    try { const seen = window.__sendlyStaticJsSeen; if (seen && seen.forEach) seen.forEach(u => done.add(String(u))); } catch (_) { /* optional */ }
    const tags = Array.from(document.querySelectorAll('script[src*="/static/js/"], link[href*="/static/js/"]'))
      .map(el => el.src || el.href)
      .filter(u => u && !done.has(u));
    const names = getInFlightChunkNames().map(n => `chunk:${n}`);
    return tags.concat(names)
      .slice(0, 12)
      .map(u => String(u).slice(0, 200));
  });
  set("lastRes", () => (performance.getEntriesByType("resource") || []).slice(-10).map(r => String(r.name).slice(0, 200)));
  return s;
}

async function reportBootStall(persistent) {
  try {
    if (!withinDailyCap()) return;   // shares the 20/device/day crash cap
    const snap = bootStallSnapshot(persistent);
    const record = {
      id: makeCrashId(),
      message: `boot-stall: ${snap.stage || "unknown"}${persistent ? " (persistent)" : ""}`,
      stack: "",
      url: typeof window !== "undefined" ? window.location.href.slice(0, 500) : null,
      uid: auth.currentUser?.uid || null,
      userAgent: typeof navigator !== "undefined" ? (navigator.userAgent || "").slice(0, 500) : null,
      source: "boot-stall",
      componentStack: "",
      extra: snap,
      timestampMs: Date.now(),
      appVersion: version || null,
      commitHash: commitHash || null,
      buildTime: buildTime || null,
    };
    enqueuePending(record);                                   // durable — survives the white screen
    try { await writeCrash(record); dropPending(record.id); } // best-effort now; may hang on a dead link
    catch (e) { if (isPermanentWriteError(e)) dropPending(record.id); /* else stays queued for the next boot's flush */ }
  } catch (_) { /* diagnostics must never throw */ }
}

/**
 * Arm the boot-stall watchdog. Call once at startup (after
 * installGlobalErrorHandlers). Fires only if the app never signals a successful
 * boot while a full-page loader is still on screen — i.e. a real white-screen
 * hang, not a normal (fast) boot or a login screen.
 */
export function installBootWatchdog() {
  if (typeof window === "undefined") return;
  // Skip unattended kiosk / TV displays — they legitimately sit on loaders
  // during reconnects and there is no user to mislead with a white screen.
  try {
    if (/\/gym\/[^/]+\/(dashboard|competition\/[^/]+\/tv)$|\/tv-control\//.test(location.pathname)) return;
  } catch (_) { /* location unavailable */ }

  const stuck = () => {
    try { return !window.__sendlyBooted && (window.__sendlyLoaders || 0) > 0; }
    catch (_) { return false; }
  };
  const isVisible = () => {
    try { return document.visibilityState === "visible"; } catch (_) { return true; }
  };

  // ── Backgrounded pages are this watchdog's main false-positive source ──
  // A hidden page sits on a loader for reasons that have nothing to do with a
  // hang: the browser deprioritises its chunk fetches AND throttles the timers
  // that measure them — including lazyChunk's 15s deadline, whose entire job is
  // to turn a hung chunk into a recoverable error BEFORE we ever report it. Both
  // production "boot-stall: suspense" rows on /profile were hidden pages whose
  // 20s timer fired ~4s late and which had un-stuck themselves by the 40s check.
  // The snapshot cannot separate the two after the fact, so the check has to.
  //
  // Rule: only ever judge a VISIBLE page, and require it to have been visible
  // for a grace period first, so work the background throttled has a chance to
  // land before we call it a hang.
  //
  // The state is a TIMESTAMP, not a "was hidden" flag: the 20s and 40s checks
  // both consult it, and a flag consumed by whichever ran first let the other
  // one report with no grace at all — the exact false positive this guards.
  let visibleSince = isVisible() ? Date.now() : 0;   // 0 = hidden right now
  if (!visibleSince) bootSawHidden = true;

  const listeners = [];
  const on = (fn) => {
    try { document.addEventListener("visibilitychange", fn); listeners.push(fn); }
    catch (_) { /* no document */ }
  };
  const off = (fn) => {
    try { document.removeEventListener("visibilitychange", fn); } catch (_) { /* no document */ }
  };
  bootWatchdogCleanup = () => { listeners.forEach(off); listeners.length = 0; };

  on(() => {
    if (isVisible()) visibleSince = Date.now();
    else { visibleSince = 0; bootSawHidden = true; }
  });

  const whenVisible = (fn) => {
    const onChange = () => { if (isVisible()) { off(onChange); fn(); } };
    on(onChange);
  };

  const evaluate = (persistent, deferrals) => {
    if (!stuck()) return;                       // booted, or the loader cleared
    if (!visibleSince) { whenVisible(() => evaluate(persistent, deferrals)); return; }
    const visibleFor = Date.now() - visibleSince;
    if (visibleFor < BOOT_STALL_HIDDEN_GRACE_MS && deferrals < BOOT_STALL_MAX_DEFERRALS) {
      setTimeout(() => evaluate(persistent, deferrals + 1), BOOT_STALL_HIDDEN_GRACE_MS - visibleFor);
      return;
    }
    reportBootStall(persistent);
  };

  setTimeout(() => evaluate(false, 0), BOOT_STALL_MS);
  setTimeout(() => evaluate(true, 0), BOOT_STALL_PERSIST_MS);
}

/**
 * Install global error handlers for errors outside the React tree.
 * Call once at app startup (e.g., in index.js).
 */
export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    const msg = event.error?.message || event.message || "";
    const filename = event.filename || "";
    // Cross-origin script error — browser redacts message/stack/filename for security.
    // Source is almost always a third-party script (FB in-app browser pixel, extension) — no diagnostic value.
    if (msg === "Script error." && !filename && !event.error?.stack) return;
    // Stale chunk: server returned HTML instead of JS after deploy
    if (isStaleChunkError(msg, filename)) {
      if (handleStaleChunkReload(extractChunkUrl(msg, filename), isUrllessChunkMessage(msg))) return;
    }
    logCrashError(event.error || event.message, {
      source: "window.onerror",
      info: `${filename}:${event.lineno || ""}:${event.colno || ""}`,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    // The late answer of a popup the Auth SDK already declared closed: the
    // caller that armed the window reports the real outcome, so this is not a
    // crash. Only inside that window — the same message outside it is logged.
    if (claimLatePopupAssertion(reason)) return;
    const msg = reason?.message || String(reason);
    // Stale chunk: dynamic import() fails with chunk loading error
    if (isStaleChunkError(msg, "")) {
      if (handleStaleChunkReload(extractChunkUrl(msg, ""), isUrllessChunkMessage(msg))) return;
    }
    logCrashError(reason instanceof Error ? reason : String(reason), {
      source: "unhandledrejection",
    });
  });

  // Resend crashes a previous session persisted but couldn't deliver (reload
  // race / dead Firestore). Deferred so it doesn't compete with boot and so
  // auth/Firestore have settled; if it's still offline it stays queued and the
  // next boot retries.
  const flush = () => { flushPendingCrashes(); };
  if ("requestIdleCallback" in window) { window.requestIdleCallback(flush, { timeout: 8000 }); }
  else { setTimeout(flush, 4000); }
}
